import { UseMutateFunction, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  getHostingAdvancedServerQueryKey,
  getHostingSitesV2QueryKey,
  useHostingAdvancedServerStatus,
} from '@newfold/huapi-js';
import {
  HostingAdvancedServerStatus200,
  HostingAdvancedServerStatusBody,
} from '@newfold/huapi-js/src/index.schemas';

import { useAlert } from '~/components/Alerts/alertsStore';
import { convertDotsToHyphens } from '~/utils/hosting/helpers';

interface UseReimageMutationParams {
  hostingId: string;
  onClose: () => void;
}

type ReimageMutationFunction = UseMutateFunction<
  AxiosResponse<HostingAdvancedServerStatus200>,
  AxiosError,
  { hostingId: String; data: HostingAdvancedServerStatusBody },
  unknown
>;

interface UseReimageMutationReturn {
  mutate: ReimageMutationFunction;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Hook to handle reimage mutation with common success/error handling
 * Handles query invalidation, alerts, navigation, and modal closing
 */
export const useReimageMutation = ({
  hostingId,
  onClose,
}: UseReimageMutationParams): UseReimageMutationReturn => {
  const queryClient = useQueryClient();
  const generateAlert = useAlert();
  const navigate = useNavigate();

  const { t: tSelfManaged } = useTranslation('selfManagedHosting');
  const hostingSitesQueryKey = getHostingSitesV2QueryKey(hostingId);
  const advancedServerQueryKey = getHostingAdvancedServerQueryKey(
    // @ts-expect-error: hostingId is a string
    hostingId,
  );

  const mutationResult = useHostingAdvancedServerStatus({
    mutation: {
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: advancedServerQueryKey });
        queryClient.invalidateQueries({ queryKey: hostingSitesQueryKey });

        // Show success alert
        generateAlert({
          severity: 'success',
          description: (
            <Trans
              t={tSelfManaged}
              i18nKey={'reimageInProgress'}
              components={{ b: <b /> }}
            />
          ),
          showCloseBtn: true,
        });

        // Close modal and redirect to self-managed-hosting
        // Server status will be checked on route load to determine if polling should start
        const hostingIdWithHyphens = convertDotsToHyphens(
          typeof hostingId === 'number' ? String(hostingId) : hostingId,
        );
        navigate(`/${hostingIdWithHyphens}/self-managed-hosting/overview`);
      },
      onError: () => {
        generateAlert({
          severity: 'error',
          description: (
            <Trans
              t={tSelfManaged}
              i18nKey={'reimageFailed'}
              components={{ b: <b /> }}
            />
          ),
          showCloseBtn: true,
        });
      },
      onSettled: () => {
        onClose();
      },
    },
  });

  return {
    // @ts-expect-error huapi mutate hostingId type mismatch
    mutate: mutationResult.mutate,
    isLoading: !!mutationResult.isPending,
    isError: !!mutationResult.error,
  };
};
